import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Alert, Button, ButtonGroup, Col, Form, Row } from "react-bootstrap";
import SvgArrowsWhite from "../../Shared/Icons/ArrowsWhite";
import { getStrings } from "../../Shared/Reservation/StringResources";
import { StopSelector } from "../Components/StopSelector";
import { Direction, ISearchState, getApiCallState, useStateSelector } from "../Models";
import { setOrigin, setDestination, setTwoWay, setDepartureDate, setReturnDate, addPassenger, removePassenger, setPassengerType, searchJourney, loadCalendar, setTravelWithPet, setInterrail } from "./Actions";
import SvgArrowGreen from "../../Shared/Icons/ArrowGreen";
import SvgArrowsGreen from "../../Shared/Icons/ArrowsGreen";
import { DateSelector } from "../Components/DateSelector";
import { BookingFooter } from "../Components/BookingFooter";
import dayjs from "dayjs";
import { PassengerSelector } from "./PassengerSelector";
import { ValidateInterrailModal } from "./ValidateInterrail";

const strings = getStrings().component.searchForm;

export const isValidSearch = (searchState: ISearchState) => {
	//Avgångsdatum och stationer måste alltid finnas
	if (!searchState?.origin || !searchState.destination || !searchState.departureDate || searchState.origin.code === searchState.destination.code)
		return false;

	//Om det är tur och retur måste hemresedatum finnas
	if (searchState.isTwoWay && !searchState.returnDate)
		return false;

	//Avresedatum får inte vara passerat
	let departure = dayjs(searchState.departureDate);
	if (departure.isBefore(dayjs(), "day"))
		return false;

	//Hemresedatum får inte vara före avresedatum
	if (searchState.isTwoWay && departure.isAfter(searchState.returnDate, "day"))
		return false;

	//Annars är det ok!
	return true;
}

export const Search = () => {

	const dispatch = useDispatch();

	const searchState = useStateSelector(state => state.search);
	const apicalls = getApiCallState();
	const loadStopsState = apicalls.getLoadingState("loadStops");

	const requireTwoWay = false; //TODO: Detta kanske måste finnas logik för igen

	const validSearch = useMemo(() => isValidSearch(searchState), [searchState]);

	const [outboundCalendarPage, setOutboundCalendarPage] = useState<string>();
	const [inboundCalendarPage, setInboundCalendarPage] = useState<string>();
	const [showInterrail, setShowInterrail] = useState(false);

	const swapStops = () => {
		dispatch(setOrigin(searchState.destination));
		dispatch(setDestination(searchState.origin));
	};

	const calendarNavigation = (direction: Direction, date: string) => {
		// console.debug("calendarNavigation", direction, date);

		if (direction === "outbound")
			setOutboundCalendarPage(date);
		else
			setInboundCalendarPage(date);
	}

	const submit = () => {
		if (!validSearch)
			return;

		if (searchState.interrail && !searchState.interrailValidation?.isValid) {
			setShowInterrail(true);
			return;
		}

		dispatch(searchJourney(searchState));
	};

	useEffect(() => {
		if (!searchState.isTwoWay && searchState.returnDate)
			dispatch(setReturnDate(null));
		else if (searchState.isTwoWay && !searchState.returnDate)
			dispatch(setReturnDate(searchState.departureDate));
	}, [searchState.isTwoWay]);

	useEffect(() => {
		//TODO: Ladda inte kalendern om vi redan har den här månaden!
		if (outboundCalendarPage && shouldLoadCalendar(searchState, "outbound", outboundCalendarPage))
			dispatch(loadCalendar("outbound", outboundCalendarPage, searchState));

	}, [outboundCalendarPage, searchState.origin, searchState.destination]);

	useEffect(() => {
		//TODO: Ladda inte kalendern om vi redan har den här månaden!
		if (inboundCalendarPage && shouldLoadCalendar(searchState, "inbound", inboundCalendarPage))
			dispatch(loadCalendar("inbound", inboundCalendarPage, searchState));

	}, [inboundCalendarPage, searchState.origin, searchState.destination]);

	useEffect(() => {
		//Autoposta när interrailvalideringen är klar
		if (searchState.interrailValidation?.isValid && submit && showInterrail)
		{
			setShowInterrail(false);
			submit();
		}
	}, [searchState.interrailValidation]);

	useEffect(() => {
		//Kryssa ur interrail om tur- och retur
		if (searchState.isTwoWay && searchState.interrail)
			dispatch(setInterrail(false));
	}, [searchState.isTwoWay]);

	return <>
		<div className="body">
			<Form className="search-form content">

				<Row>
					<Col lg={6} className="utresa">
						<StopSelector label={strings.travelFrom} placeholder={strings.locationFromPlaceholder} stops={searchState.stops} disabled={!searchState.stops}
							selected={searchState.origin} disabledStop={searchState.destination} onStopSelected={stop => dispatch(setOrigin(stop))} />
						<button type="button" className="btn btn-link swapbutton" onClick={swapStops}><SvgArrowsWhite /></button>
					</Col>

					<Col lg={6} className="hemresa">
						<StopSelector label={strings.travelTo} placeholder={strings.locationToPlaceholder} stops={searchState.stops} disabled={!searchState.stops}
							selected={searchState.destination} disabledStop={searchState.origin} onStopSelected={stop => dispatch(setDestination(stop))} />
					</Col>
				</Row>
				{/* } */}
				{/* { loadStopsState === "loading" && <SpinnerSmall /> } */}
				{ loadStopsState === "error" && <Alert variant="danger">Unable to load stops from API.</Alert> }
				<Row className="mt-3 mb-3">
					<Col className="text-center">
						<ButtonGroup>
							<Button variant="outline-secondary" disabled={requireTwoWay} active={!searchState.isTwoWay} onClick={() => dispatch(setTwoWay(false))}>
								<SvgArrowGreen /> {strings.single}
							</Button>
							<Button variant="outline-secondary" active={searchState.isTwoWay} onClick={() => dispatch(setTwoWay(true))}>
								<SvgArrowsGreen /> {strings.return}
							</Button>
						</ButtonGroup>
					</Col>
				</Row>

				<Alert variant="warning" show={requireTwoWay}><span dangerouslySetInnerHTML={ { __html: strings.requireTwoWay}}></span></Alert>

				<Row>
					<Col lg={6}>
						<section>
							<h2>{strings.departureDate}</h2>
							<DateSelector id="departure-calendar" onDateSelected={date => dispatch(setDepartureDate(date))} selectedDate={searchState.departureDate}
								onNavigation={date => calendarNavigation("outbound", date)}
								departureCalendar={searchState.outboundCalendar?.days} />
							{/* <TimeSelector onTimeSelected={time => dispatch(selectTime(time, "outbound"))} selectedTime={searchState.departureTime} /> */}
						</section>
					</Col>
					{ searchState.isTwoWay && <Col lg={6}>
						<section>
							<h2>{strings.returnDate}</h2>
							<DateSelector id="return-calendar" onDateSelected={date => dispatch(setReturnDate(date))} selectedDate={searchState.returnDate} minDate={searchState.departureDate}
								onNavigation={date => calendarNavigation("inbound", date)}
								departureCalendar={searchState.inboundCalendar?.days} />
						</section>
					</Col> }
				</Row>

				<section>
					<h2>{strings.passengers}</h2>
					<Row>
						<Col md={6} className="mb-3">
							<PassengerSelector passengers={searchState.passengers} passengerTypes={searchState.passengerTypes}
									onSetPassengerType={(index, type) => dispatch(setPassengerType(index, type))}
									// onSetPassengerAge={(index, age) => dispatch(setPassengerAge(index, age))}
									onAddPassenger={() => dispatch(addPassenger())} onRemovePassenger={index => dispatch(removePassenger(index))} />
							<Form.Check custom type="checkbox" id="petcheck" label={strings.travelWithAnimals} className="mt-3"
								checked={searchState.travelWithPet} onChange={e => dispatch(setTravelWithPet(e.target.checked))} />
							<div className="mt-2">
								<Form.Check custom type="checkbox" id="cbInterrail" disabled={searchState.isTwoWay} label={strings.travelWithInterrailCard}
									checked={searchState.interrail ?? false} onChange={() => dispatch(setInterrail(!searchState.interrail))}></Form.Check>
								{ searchState.isTwoWay && <Form.Text muted>{strings.interrailReturnWarning}</Form.Text>}
							</div>
						</Col>

					</Row>
				</section>

				{/* <pre className="mt-4">
					<code>
						origin: {searchState.origin?.code}<br />
						destination: {searchState.destination?.code}<br />
						isTwoWay: {searchState.isTwoWay ? "true" : "false"}<br />
						departureDate: {searchState.departureDate}<br />
						returnDate: {searchState.returnDate}<br />
						passengers: {searchState.passengers?.map(x => x.type?.code).join(", ")}
					</code>
				</pre> */}
			</Form>
			<BookingFooter text={strings.searchButtonText} onSubmit={validSearch ? submit : undefined} />
		</div>

		<ValidateInterrailModal show={showInterrail} onClosed={() => setShowInterrail(false)} />
	</>;
};

function shouldLoadCalendar(searchState: ISearchState, direction: Direction, date: string) {

	//Om vi inte har all data ska vi förstås inte ladda någon kalender
	if (!searchState.origin?.code || !searchState.destination?.code || searchState.passengers.length === 0)
		return false;

	//Om origin är samma som destination ska vi inte ladda någon kalender
	if (searchState.destination.code === searchState.origin.code)
		return false;

	const currentCalendar = direction === "outbound"? searchState.outboundCalendar : searchState.inboundCalendar;

	//Om vi inte har någon kalender, eller om vi har ändrat origin eller destination ska vi alltid ladda kalender
	if (!currentCalendar || currentCalendar.origin !== searchState.origin.code || currentCalendar.destination !== searchState.destination.code)
		return true;

	//Om origin och destination inte ändrats behöver vi bara ladda kalendern om datumet inte redan är laddat
	//Eftersom vi alltid laddar hela månader kan vi alltså kolla om vi har någon dag är i samma månad som vi tittar på
	//Detta kommer förstås inte fånga månader helt utan trafik, men det kan vi leva med så länge.
	const firstDayOfMonth = dayjs(date).set("date", 1);
	const datesInCalendar = Object.getOwnPropertyNames(currentCalendar.days);
	return !datesInCalendar.some(d => firstDayOfMonth.isSame(d, "month"));

	//TODO: När vi börjar bry oss om priser måste vi också ta hänsyn till resenärererna.
}